import { useCallback, useEffect } from "react";
import {
  Container,
  Row,
  Form,
  FloatingLabel,
  Button,
  Spinner,
  Image,
} from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import styles from "./resetPasswordPage.module.scss";
import resetPasswordPageSlice from "./store/slice";
import Logo from "../../images/logo-colour.svg";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { showError } from "../toaster/store/slice";

export type ResetPasswordFormValues = {
  email: string;
  password: string;
  password_confirmation: string;
  token: string;
};

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.resetPasswordPage.isLoading);
  const requestSent = useSelector(
    (state) => state.resetPasswordPage.requestSent
  );

  const sendRequest = useCallback(
    (data: ResetPasswordFormValues) =>
      dispatch(resetPasswordPageSlice.actions.sendRequest(data)),
    [dispatch]
  );

  const displayError = useCallback(
    (text: string) => dispatch(showError(text)),
    [dispatch]
  );

  const reset = useCallback(
    () => dispatch(resetPasswordPageSlice.actions.reset()),
    [dispatch]
  );

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<ResetPasswordFormValues>();

  const onSubmit: SubmitHandler<ResetPasswordFormValues> = (data) => {
    if (token) {
      sendRequest({ ...data, token });
    }
  };

  useEffect(() => {
    if (requestSent) {
      navigate("/");
    }

    return () => {
      reset();
    };
  }, [navigate, requestSent, reset]);

  useEffect(() => {
    if (!token) {
      displayError("Reset token not found.");

      navigate("/");
    }
  }, [token, navigate, displayError]);

  return (
    <Container fluid className={styles.container}>
      <Row className={styles.row}>
        <Form
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <Image alt="logo" src={Logo} className={styles.logo} />

          <FloatingLabel
            controlId="email"
            label="Email address"
            className={styles.input}
          >
            <Form.Control
              type="email"
              placeholder="name@example.com"
              isInvalid={!!errors.email}
              {...register("email", {
                required: true,
              })}
              maxLength={100}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid" className={styles.feedback}>
              {errors.email?.type === "required" && "Required."}
              {errors.email?.type === "pattern" && "Invalid email."}
            </Form.Control.Feedback>
          </FloatingLabel>

          <FloatingLabel
            controlId="password"
            label="Password"
            className={styles.input}
          >
            <Form.Control
              type="password"
              placeholder="Password"
              isInvalid={!!errors.password}
              {...register("password", {
                required: true,
                minLength: 8,
                validate: {
                  containsUpper: (value) => /[A-Z]/.test(value),
                  containsLower: (value) => /[a-z]/.test(value),
                  containsDigits: (value) => /[0-9]/.test(value),
                },
              })}
              maxLength={100}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid" className={styles.feedback}>
              {errors.password?.type === "required" && "Required."}
              {errors.password?.type === "minLength" &&
                "Minimum 8 characteres required."}
              {errors.password?.type === "containsUpper" &&
                "At least 1 upper case letter required."}
              {errors.password?.type === "containsLower" &&
                "At least 1 lower case letter required."}
              {errors.password?.type === "containsDigits" &&
                "At least 1 number required."}
            </Form.Control.Feedback>
          </FloatingLabel>

          <FloatingLabel
            controlId="password_confirmation"
            label="Confirm password"
            className={styles.input}
          >
            <Form.Control
              type="password"
              placeholder="Confirm password"
              isInvalid={!!errors.password_confirmation}
              {...register("password_confirmation", {
                required: true,
                minLength: 8,
                validate: {
                  validate: (value) => value === watch("password"),
                },
              })}
              maxLength={100}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid" className={styles.feedback}>
              {errors.password_confirmation?.type === "required" && "Required."}
              {errors.password_confirmation?.type === "validate" &&
                "Passwords doesn't match."}
              {errors.password_confirmation?.type === "minLength" &&
                "Minimum 8 characteres required."}
            </Form.Control.Feedback>
          </FloatingLabel>

          <Button
            type="submit"
            size="lg"
            className={styles.submit}
            disabled={isLoading}
          >
            Reset
            {isLoading && (
              <Spinner
                size="sm"
                animation="border"
                className={styles.spinner}
              />
            )}
          </Button>

          <div className={styles.goBack}>
            <Link to="/">Go back</Link>
          </div>

          <p className={styles.copyright}>
            © {moment().format("YYYY")} Girteka Competence Center
          </p>
        </Form>
      </Row>
    </Container>
  );
};

export default ResetPasswordPage;
